import OAuth2State from './oauth2_state';
import {SYSTEM_EVENTS} from '../../../events/events';
import DOM from '../../../utils/dom';
import Event from '../../../utils/event';
import Token from '../tokens/token';
import {OAUTH2_CSS} from '../oauth_const';
import EmptyToken from '../tokens/empty_token';
import {TimeSpan} from "@sparkware/app-utils";
import {REFRESH_TOKEN_REASONS} from "./oauth2_authorized_state";

export default class OAuth2AnonymousState extends OAuth2State {
    static CLASS = 'OAuth2AnonymousState';

    _stateChangedCallback = null;
    _backendProxy = null;
    _oauth2StateFactory = null;
    _emptyToken = null;
    _serviceFactory = null;

    init(params){
        super.init(params);
        this._emptyToken = EmptyToken.create();
        this._stateChangedCallback = params.stateChangedCallback;
        this._backendProxy = params.backendProxy;
        this._oauth2StateFactory = params.oauth2StateFactory;
        this._serviceFactory = params.serviceFactory;

        this._subscribe();

        DOM.removeClass(DOM.getBody(), OAUTH2_CSS.AUTHORIZED_USER_CLASS);
    }

    isAuthorized() {
        return false;
    }

    _subscribe() {
        Event.on(SYSTEM_EVENTS.LOCAL_STORAGE_CHANGED, this._onLocalStorageChanged, this);
        Event.on(SYSTEM_EVENTS.RUN_AUTOLOGIN, this._onRunAutologin, this);
        Event.on(SYSTEM_EVENTS.REGISTRATION_COMPLETED, this._onRegistrationCompleted, this);
        Event.on(SYSTEM_EVENTS.RUN_AUTOLOGIN_TOUCH_ID, this._onAutologinWithTouchId, this);
        Event.on(SYSTEM_EVENTS.APP_RESUMED, this._onAppResumed, this);

    }

    _unsubscribe() {
        Event.off(SYSTEM_EVENTS.LOCAL_STORAGE_CHANGED, this._onLocalStorageChanged, this);
        Event.off(SYSTEM_EVENTS.RUN_AUTOLOGIN, this._onRunAutologin, this);
        Event.off(SYSTEM_EVENTS.REGISTRATION_COMPLETED, this._onRegistrationCompleted, this);
        Event.off(SYSTEM_EVENTS.RUN_AUTOLOGIN_TOUCH_ID, this._onAutologinWithTouchId, this);
        Event.off(SYSTEM_EVENTS.APP_RESUMED, this._onAppResumed, this);
    }

    dispose() {
        this._unsubscribe();
    }

    _onLocalStorageChanged() {
        if(Token.isInStorage()) { //just in case this event is called more than once (it happens on IE)
            Event.off(SYSTEM_EVENTS.LOCAL_STORAGE_CHANGED, this._onLocalStorageChanged, this);
            let token = Token.fromStorage();
            if (!token.isExpired()) {
                this._switchToAuthorizedState({
                    token: token
                });
            }
        }
    }

    _onRunAutologin(token) {
        this.autologinWithToken(token);
    }

    _onRegistrationCompleted(postMessageData) {
        this.autologinWithToken(postMessageData.UserInfo,null,postMessageData.SourceProductPackage, null, null, null, true);
    }

    _onAutologinWithTouchId(touchIdToken) {
        this._backendProxy.autoLoginWithTouchId(touchIdToken)
            .then(tokenResponse => {
                this._serviceFactory.webLoginService().closeLoginForm();
                return this._switchToAuthorizedState({
                    tokenData: tokenResponse
                });
            })
            .catch(responseError => {
                this._handleAutologinError(responseError);
            });

    }

    autologinWithToken(autologinToken, sessionTimerOffset, sourceProductPackage, brandId, sourceSubBrandId, targetSubBrandId, fromRegistration= false) {

        Event.fire(SYSTEM_EVENTS.ON_RUN_AUTOLOGIN);
        if(fromRegistration) {
            Event.fire(SYSTEM_EVENTS.BEGIN_LOGIN_AFTER_REGISTRATION);
        }

        return this._backendProxy.autoLoginWithToken(autologinToken, sessionTimerOffset, sourceProductPackage, brandId, sourceSubBrandId, targetSubBrandId)
            .then((tokenResponse) => {


                this._serviceFactory.webLoginService().closeLoginForm();
                const result = this._switchToAuthorizedState({
                    tokenData: tokenResponse,
                    fromRegistration: fromRegistration
                });

                if(fromRegistration) {
                    Event.fire(SYSTEM_EVENTS.LOGIN_COMPLETED_AFTER_REGISTERATION);
                }

                return result;
            })
            .catch(responseError => {
                this._handleAutologinError(responseError);
                if(fromRegistration) {
                    Event.fire(SYSTEM_EVENTS.LOGIN_FAILED_AFTER_REGISTRATION);
                }
            });
    }

    _handleAutologinError(responseError) {
        Event.fire(SYSTEM_EVENTS.TOUCH_ID_LOGIN_ERROR, responseError.error_code);
        this._serviceFactory.webLoginService().processLoginResponse(responseError);
    }

    /**
     *
     * @param params: {
     *  token: Token
     *  tokenData: json
     * }
     * @returns {*}
     * @private
     */
    _switchToAuthorizedState(params) {
        Event.fire(SYSTEM_EVENTS.ON_BEFORE_OAUTH_STATE_CHANGE);
        this.dispose();

        let newState = null;

        if(params.tokenData) {
            newState = this._oauth2StateFactory.createAuthorizedStateForNewLogin(params.tokenData, params.hasRememberMe, params.sessionTimerOffset);
        } else if(params.token) {
            newState = this._oauth2StateFactory.createAuthorizedStateForExistingToken(params.token);
        } else {
            this.throwError('148',
                'When switching to authorized state you must provide either token or tokenData parameters',
                {initializationParams: params});
        }



        this._stateChangedCallback(newState, {fromRegistration: params.fromRegistration});

        return newState;
    }

    _closeAllOverlays() {
        Event.fire(SYSTEM_EVENTS.CLOSE_ALL_OVERLAY);
    }

    _onAppResumed() {
        let token = Token.fromStorage();
        if(!token.isEmpty()) {
            if(token.isExpired()) {
                this._closeAllOverlays();
                this.tryToRememberMe();
            } else {
                this._switchToAuthorizedState({
                    token: token
                });
            }

        }
    }

    tryToRememberMe() {
        let token = Token.fromStorage();
        if(token.isEmpty()) {
            return Promise.reject('Can`t remember me because there is no token in the local storage');
        }

        return this._backendProxy.refreshToken(token.getRefreshToken(), {reason: REFRESH_TOKEN_REASONS.try_remember_me})
            .then(tokenData => {
                this._switchToAuthorizedState({
                    tokenData: tokenData
                });
            })
            .catch(err => {
                token.delete();
                this.reThrowError('193', 'OAuth2AnonymousState.tryToRememberMe failed!', err);
            });
    }

    login(credentials) {
        return this._backendProxy.loginWithPassword(credentials)
            .then(tokenData => {
                this._switchToAuthorizedState({
                    tokenData: tokenData,
                    hasRememberMe: credentials.remember_me
                });
            });
    }

    logout() {
        return Promise.resolve();
    }

    getDecodedAccessToken() {
        return this._emptyToken.getDecodedAccessToken();
    }

    getTokenAuthorizeHeader() {
        // This is for features like Remember Me to work
        return Token.fromStorage().getHttpAuthorizationHeader();
    }

    extractTokenProperty(tokenProperty) {
        return this._emptyToken.getFieldValue(tokenProperty);
    }

    isVIP() {
        return false;
    }

    getUserData() {
        return this._emptyToken.getUserData();
    }

    getAccountData() {
        return this._emptyToken.getAccountData();
    }

    updateTokenData(filedName, fieldValue) {
        this._emptyToken.setFieldValue(filedName, fieldValue);
    }

    getRefreshToken() {
        return null;
    }

    refreshToken() {
        return Promise.resolve();
    }

    getTimeSinceLogin() {
        return Promise.resolve(TimeSpan.fromMilliseconds(0));
    }

    getLoginType() {
        return null;
    }

    getSourceProductPackage() {
        return null;
    }

    onInitComplete(fromOnInitCompleteEvent) {
        // nothing to do here
    }
}
